import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { FilterBar } from '@/components/composed/FilterBar';
import { Button } from '@/components/ui/button';
import {
  ClientTable,
  ClientDetailSheet,
  ClientFormDialog,
  clientListQuery,
} from '@/features/clients';
import type { Client, ClientListParams } from '@/lib/api/domains/clients';
import { clientId } from '@/lib/api/ids';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  sort: z.string().optional(),
  q: z.string().optional(),
  inspect: z.string().optional(),
});

export const Route = createFileRoute('/_auth/clients/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(clientListQuery(toParams(deps))),
  component: ClientsPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function toParams(search: z.infer<typeof searchSchema>): ClientListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.q ? { q: search.q } : {}),
  };
}

function ClientsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useAuthStore((s) => s.session);
  const [createOpen, setCreateOpen] = useState(false);

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  usePageMeta({ title: 'Clients' });

  return (
    <Page bleed>
      <PageHeader
        title="Clients"
        actions={
          canEdit(session) ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              New client
            </Button>
          ) : null
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by name…"
          onClear={
            search.q ? () => setSearch({ q: undefined, page: 1 }) : undefined
          }
        />
        <ClientTable
          params={toParams(search)}
          onPageChange={(page) => setSearch({ page })}
          onRowClick={(client: Client) => setSearch({ inspect: client.id })}
          onEdit={(client: Client) => setSearch({ inspect: client.id })}
        />
      </Section>

      <ClientDetailSheet
        clientId={search.inspect ? clientId(search.inspect) : null}
        open={Boolean(search.inspect)}
        onOpenChange={(open) =>
          setSearch({ inspect: open ? search.inspect : undefined })
        }
      />

      <ClientFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}
